import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Package, 
  ShoppingBag,
  Loader,
  Heart,
  Coffee,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  User
} from 'lucide-react';
import ContentItemCard from '../components/ContentItemCard';
import { authService } from '../authService'
import { AUTH_CONFIG } from '../config'

// Purchase Card Component
const PurchaseCard = ({ purchase, items, onCustomItemAction }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  // Format the date
  const formatDate = (dateString) => {
    if (!dateString) return 'Data sconosciuta';
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Truncate description if needed
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get product description from the enriched purchase data
  const productDescription = purchase.product?.description;
  const productName = purchase.product?.name || purchase.product_name || 'Prodotto';

  // Sort items by visualization_order if available
  const sortedItems = [...items].sort((a, b) => {
    // Default to 0 if visualization_order is not available
    const orderA = a.visualization_order !== undefined ? a.visualization_order : 0;
    const orderB = b.visualization_order !== undefined ? b.visualization_order : 0;
    return orderA - orderB;
  });

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`border rounded-xl overflow-hidden shadow-sm mb-6 ${
        isExpanded ? 'border-green-200 bg-white' : 'border-gray-200 bg-white hover:border-green-100'
      }`}
    >
      {/* Purchase header */}
      <div 
        className={`flex items-center justify-between p-5 cursor-pointer transition-all ${
          isExpanded ? 'bg-green-50/50' : 'hover:bg-green-50/30'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <div className="bg-white text-green-600 p-3 rounded-xl mr-4 shadow-sm border border-green-100">
            <ShoppingBag size={20} />
          </div>
          <div>
            <h2 className="font-bold text-lg text-gray-800">
              {productName}
            </h2>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Calendar size={14} className="mr-1" />
              <span>Acquistato il {formatDate(purchase.payment_date || purchase.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <div className={`p-2 rounded-full ${isExpanded ? 'bg-green-50 border-green-200' : 'bg-white'} border border-green-100`}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        </div>
      </div>
      
      {/* Purchase details */}
      {isExpanded && (
        <div className="p-5 border-t border-green-100 bg-white">
          {/* Product description */}
          {productDescription && (
            <div className="mb-6 bg-green-50/50 p-4 rounded-xl border border-green-100">
              <div className="flex items-center mb-2">
                <Info size={16} className="text-green-600 mr-2" />
                <h3 className="font-medium text-gray-800">Descrizione:</h3>
              </div>
              <div className="text-gray-700">
                {showFullDescription ? productDescription : truncateText(productDescription)}
                {productDescription.length > 150 && (
                  <button 
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="ml-2 text-green-600 font-medium hover:underline"
                  >
                    {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                  </button>
                )}
              </div>
            </div>
          )}
          
          <h3 className="font-medium mb-4 text-gray-800 flex items-center">
            <Package size={16} className="mr-2 text-green-600" />
            Contenuti disponibili:
          </h3>
          {sortedItems && sortedItems.length > 0 ? (
            <div className="space-y-3">
              {sortedItems.map(item => {
                // Add product name to each item for URL generation
                const enrichedItem = {
                  ...item,
                  product_name: productName
                };
                
                return (
                  <ContentItemCard 
                    key={item.id} 
                    item={enrichedItem} 
                    isOpen={openItemId === item.id}
                    onClick={() => setOpenItemId(openItemId === item.id ? null : item.id)}
                    onCustomAction={onCustomItemAction}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 italic p-4 bg-gray-50 rounded-xl border border-gray-100">
              Nessun contenuto disponibile per questo prodotto.
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Main Content Page Component
const IMieiContenutiPage = () => {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productItems, setProductItems] = useState({});
  const [greeting, setGreeting] = useState('');

  // Set greeting based on time of day
  useEffect(() => {
    const hour = new Date().getHours();
    let greetingMessage = '';
    
    if (hour >= 5 && hour < 12) {
      greetingMessage = 'Buongiorno';
    } else if (hour >= 12 && hour < 18) {
      greetingMessage = 'Buon pomeriggio';
    } else if (hour >= 18 && hour < 22) {
      greetingMessage = 'Buona sera';
    } else {
      greetingMessage = 'Buona tarda notte';
    }
    
    setGreeting(greetingMessage);
  }, []);
  
  // Fetch user products from backend API
  useEffect(() => {
    const fetchUserProducts = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const token = authService.getToken();
        
        // Call backend API endpoint with user ID using fetch
        const response = await fetch(
          `${AUTH_CONFIG.apiUrl}/user-products/${user.email}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Check if response was successful
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        // Parse the JSON response
        const data = await response.json();
        
        console.log("Received data:", data);
        
        // Set state with response data
        setPurchases(data.purchases || []);
        setProductItems(data.items_by_product || {});
      } catch (error) {
        console.error('Error fetching user products:', error);
        setError(error.message || 'Si è verificato un errore durante il caricamento dei tuoi prodotti');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProducts();
  }, [user]);
  
  const handleCustomItemAction = async (productId, itemId) => {
    console.log(`Custom action triggered for product ${productId}, item ${itemId}`);
    
    try {
      // Get the item details from the productItems state
      const items = productItems[productId] || [];
      const item = items.find(item => item.id === itemId);
      
      // Check if it's a PDF item (only perform direct download for PDFs)
      if (item && item.type?.toLowerCase() === 'pdf') {
        // Get session for auth header

        const token = authService.getToken();

        // Make API call to get the download URL
        const response = await fetch(
          `${AUTH_CONFIG.apiUrl}/content/content-download-url?email=${encodeURIComponent(user.email)}&product_id=${encodeURIComponent(productId)}&item_id=${encodeURIComponent(itemId)}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Check if response was successful
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        // Parse the JSON response
        const data = await response.json();
        
        // If we have a presigned URL, open it in a new tab
        if (data.presigned_url) {
          window.open(data.presigned_url, '_blank');
        } else {
          throw new Error('No download URL received');
        }
      } else {
        // For video and audio, the ContentItemCard component will handle navigation
        console.log('Non-PDF content action');
      }
    } catch (error) {
      console.error('Error handling item action:', error);
      alert(`Error: ${error.message || 'Failed to process your request'}`);
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-gradient-to-b from-green-50 to-white py-12">
        <div className="p-4 rounded-full bg-white border border-green-100 mb-5 shadow-sm">
          <Loader className="animate-spin text-green-600" size={40} />
        </div>
        <p className="text-gray-700 font-medium">Caricamento dei tuoi contenuti...</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center bg-gradient-to-b from-red-50 to-white py-12">
        <div className="text-red-500 bg-white p-5 rounded-full mb-5 shadow-sm border border-red-100">
          <AlertCircle size={40} />
        </div>
        <h2 className="text-xl font-bold text-red-700 mb-3">Si è verificato un errore</h2>
        <p className="text-gray-600 mb-5 max-w-md">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow-sm hover:shadow-md"
        >
          Riprova
        </button>
      </div>
    );
  }

  // Handle empty state
  if (purchases.length === 0) {
    return (
      <div className="min-h-content bg-gradient-to-b from-green-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back to Profile Button */}
          <div className="mb-6">
            <Link 
              to="/profilo" 
              className="inline-flex items-center px-4 py-2 bg-white text-green-600 rounded-full shadow-sm border border-green-100 hover:bg-green-50 transition-all"
            >
              <ArrowLeft size={18} className="mr-2" />
              Torna al profilo
            </Link>
          </div>
          
          <div className="flex flex-col items-center justify-center min-h-[500px] p-6 text-center">
            <div className="text-green-600 bg-white p-5 rounded-full mb-5 shadow-sm border border-green-100">
              <ShoppingBag size={40} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Nessun contenuto disponibile</h2>
            <p className="text-gray-600 mb-5 max-w-md">
              Non hai ancora effettuato acquisti o i tuoi acquisti non sono stati completati.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-content bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back to Profile Button */}
        <div className="mb-6">
          <Link 
            to="/profilo" 
            className="inline-flex items-center px-4 py-2 bg-white text-green-600 rounded-full shadow-sm border border-green-100 hover:bg-green-50 transition-all"
          >
            <ArrowLeft size={18} className="mr-2" />
            <User size={16} className="mr-1" />
            Torna al profilo
          </Link>
        </div>
        
        {/* Header with greeting */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-br from-green-100 via-green-50 to-white p-6 rounded-xl border border-green-100 shadow-sm"
        >
          <div className="flex items-center mb-3">
            <Coffee size={24} className="text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-600 bg-white px-3 py-1 rounded-full border border-green-100">
              {greeting}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">I tuoi contenuti</h1>
          <p className="text-gray-600">
            Accedi ai contenuti dei prodotti che hai acquistato. Buona fruizione!
          </p>
        </motion.div>
        
        {/* Content cards */}
        <div className="space-y-6">
          {purchases.map(purchase => (
            <PurchaseCard 
              key={purchase.id} 
              purchase={purchase} 
              items={productItems[purchase.product_id] || []}
              onCustomItemAction={handleCustomItemAction}
            />
          ))}
        </div>
        
        {/* Bottom note with Back to Profile button */}
        <div className="mt-10 flex flex-col items-center space-y-4">
          <div className="inline-flex items-center text-gray-500 bg-white px-4 py-2 rounded-full border border-gray-200 text-sm">
            <Heart size={16} className="text-green-600 mr-2" />
            Grazie per il tuo supporto!
          </div>
          
          <Link 
            to="/profilo" 
            className="inline-flex items-center px-5 py-2.5 bg-white text-green-600 rounded-full shadow-sm border border-green-100 hover:bg-green-50 transition-all"
          >
            <ArrowLeft size={18} className="mr-2" />
            Torna al profilo
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IMieiContenutiPage;